import type { WaitlistPurpose } from '@manasil/shared';
import { OTHER_PURPOSE } from '@manasil/shared';
import { useState } from 'react';
import { Shell } from '../components/Shell.js';
import { Empty, Loading } from '../components/ui.js';
import { useGoals, useWaitlist } from '../lib/queries.js';

function joined(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Marketing-site signups.
 *
 * These are NOT app users and are deliberately kept on their own screen — nothing here has
 * verified a phone number, and CLAUDE.md §2.8 is explicit that no account exists until an
 * OTP succeeds. Treating a lead as a user is how a funnel starts lying to you.
 */
export function WaitlistPage() {
  const [search, setSearch] = useState('');
  const [purpose, setPurpose] = useState<WaitlistPurpose | 'all'>('all');
  const { data: goals } = useGoals();
  const { data, isLoading } = useWaitlist({
    search,
    ...(purpose === 'all' ? {} : { purpose }),
  });

  /** Goal keys are stored; the display name lives in the goals table and can change. */
  const nameOf = (key: WaitlistPurpose): string =>
    key === OTHER_PURPOSE ? 'Other' : (goals?.find((goal) => goal.key === key)?.name.en ?? key);

  const rows = data?.items ?? [];
  const filters: WaitlistPurpose[] = [...(goals ?? []).map((goal) => goal.key), OTHER_PURPOSE];

  return (
    <Shell
      title="Waitlist"
      subtitle="People who signed up on the website. Leads, not accounts — none of these have verified a number."
    >
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <input
          className="input"
          style={{ maxWidth: 280 }}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search name, email or mobile…"
        />
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="chip"
            aria-pressed={purpose === 'all'}
            onClick={() => setPurpose('all')}
          >
            All
          </button>
          {filters.map((key) => (
            <button
              key={key}
              type="button"
              className="chip"
              aria-pressed={purpose === key}
              onClick={() => setPurpose(key)}
            >
              {nameOf(key)}
            </button>
          ))}
        </div>
        {data ? (
          <span className="hint nums" style={{ marginLeft: 'auto' }}>
            {data.total} total
          </span>
        ) : null}
      </div>

      <div className="card">
        {isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty
            mark="◇"
            title="Nobody yet"
            hint={
              search || purpose !== 'all'
                ? 'No signups match this filter.'
                : 'Signups from the website will appear here as they come in.'
            }
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Looking for</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.name}</td>
                  <td className="truncate">{entry.email}</td>
                  <td className="nums">{entry.mobile}</td>
                  <td>
                    <div className="row" style={{ gap: 5, flexWrap: 'wrap' }}>
                      {entry.purposes.map((key) => (
                        <span key={key} className="pill">
                          {nameOf(key)}
                        </span>
                      ))}
                    </div>
                    {entry.otherInterest ? (
                      <div className="hint" style={{ marginTop: 4 }}>
                        “{entry.otherInterest}”
                      </div>
                    ) : null}
                  </td>
                  <td className="hint nums">{joined(entry.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Shell>
  );
}
